import { HelpMessage } from 'utils/constants'
import { DensityLotEditPanelCollapseProps } from './types'
import { IFIReportType, LotCompactionLevel } from 'shared'
import CIMSCollapse from 'components/CIMSCollapse'
import DirtyHeader from '../../../../../../components/DirtyHeader'
import { Space, Typography } from 'antd'
import CIMSFormItem from 'components/CIMSFormItem'
import CIMSTextArea from 'components/CIMSTextArea'
import CIMSSwitch from 'components/CIMSSwitch'
import CIMSSelect from 'components/CIMSSelect'
import { useOptions } from 'components/CIMSSelect/hooks/useOptions'
import { useWatch } from 'antd/lib/form/Form'

const Help = HelpMessage.Administration.ProjectSettings.IFITracker
const {
  defaultMessages: { count, proctor, lotCompaction, testCompaction },
} = IFIReportType.Lot

const DensityLotEditPanelCollapse: React.VFC<
  DensityLotEditPanelCollapseProps
> = ({ form, dirtyPanels, openPanels, setOpenPanels }) => {
  const compactionLevelIdWatch = useWatch('compactionLevelId', form)
  const compactionOptions = useOptions({
    items: LotCompactionLevel.values,
    value: 'id',
    label: 'label',
    sort: 'label',
  })
  return (
    <CIMSCollapse
      multiple
      activeKey={openPanels}
      onChange={setOpenPanels}
      panels={[
        {
          header: {
            children: (
              <DirtyHeader
                title="Test Count"
                isDirty={dirtyPanels.includes('count')}
              />
            ),
          },
          key: `edit-test-count-${IFIReportType.Lot.id}`,
          // Must force render panels for Switch disabled checks to work on load
          forceRender: true,
          children: (
            <Space size="middle" direction="vertical">
              <Typography>
                If the number of active Density Tests is less than the Number of
                Required Tests, issue an Identified Field Issue using the
                Message.
              </Typography>
              <CIMSFormItem
                name="testCountMessage"
                label="Message"
                help={Help.FailureMessage()}
                defaultMessage={count}
              >
                <CIMSTextArea />
              </CIMSFormItem>
            </Space>
          ),
          extra: {
            children: (
              <CIMSFormItem
                noStyle
                name="testCountEnabled"
                valuePropName="checked"
              >
                <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
              </CIMSFormItem>
            ),
          },
        },
        {
          header: {
            children: (
              <DirtyHeader
                title="Proctor"
                isDirty={dirtyPanels.includes('proctor')}
              />
            ),
          },
          key: `edit-proctor-${IFIReportType.Lot.id}`,
          // Must force render panels for Switch disabled checks to work on load
          forceRender: true,
          children: (
            <Space size="middle" direction="vertical">
              <Typography>
                If closed and missing a Global Proctor Value, issue an
                Identified Field Issue description using the Message.
              </Typography>
              <CIMSFormItem
                name="proctorMessage"
                label="Message"
                help={Help.FailureMessage()}
                defaultMessage={proctor}
              >
                <CIMSTextArea />
              </CIMSFormItem>
            </Space>
          ),
          extra: {
            children: (
              <CIMSFormItem
                noStyle
                name="proctorEnabled"
                valuePropName="checked"
              >
                <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
              </CIMSFormItem>
            ),
          },
        },
        {
          header: {
            children: (
              <DirtyHeader
                title="Compaction"
                isDirty={dirtyPanels.includes('compaction')}
              />
            ),
          },
          key: `edit-compaction-${IFIReportType.Lot.id}`,
          // Must force render panels for Switch disabled checks to work on load
          forceRender: true,
          children: (
            <>
              <Space direction="vertical" size="middle">
                <Typography>
                  {
                    LotCompactionLevel.fromId(compactionLevelIdWatch)
                      ?.description
                  }
                </Typography>
                <CIMSFormItem
                  name="compactionLevelId"
                  label="Compaction Level"
                  help={Help.CompactionLevel}
                  defaultMessage="Lot"
                >
                  <CIMSSelect simple options={compactionOptions} />
                </CIMSFormItem>
              </Space>
              <CIMSFormItem
                name="compactionMessage"
                label="Message"
                help={Help.FailureMessage()}
                defaultMessage={
                  compactionLevelIdWatch === LotCompactionLevel.Lot.id
                    ? lotCompaction
                    : testCompaction
                }
              >
                <CIMSTextArea />
              </CIMSFormItem>
            </>
          ),
          extra: {
            children: (
              <CIMSFormItem
                noStyle
                name="compactionEnabled"
                valuePropName="checked"
              >
                <CIMSSwitch onClick={(_, event) => event.stopPropagation()} />
              </CIMSFormItem>
            ),
          },
        },
      ]}
    />
  )
}

export default DensityLotEditPanelCollapse
